import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, Send, Loader2, X, UserPlus,
  BellOff, Bell, Inbox, Search, ChevronDown,
  ChevronLeft, ChevronRight, Pin, PinOff
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useAuth } from '../context/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMsgTime(dateStr) {
  if (!dateStr) return '';
  const d   = new Date(dateStr);
  const now = new Date();
  const isToday     = d.toDateString() === now.toDateString();
  const yesterday   = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (isToday)     return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (isYesterday) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function firstPrenom(nom) {
  if (!nom) return '';
  const parts = nom.trim().split(' ');
  if (parts.length === 1) return parts[0];
  // Format "NOM Prénom" (majuscules) → retourner la suite
  if (parts[0] === parts[0].toUpperCase() && parts[0].length > 1) return parts.slice(1).join(' ');
  // Format "Prénom NOM" → retourner le premier mot
  return parts[0];
}

// Rendu inline des @mentions en amber
function renderMentions(text, onMentionClick) {
  if (!text) return null;
  return text.split(/(@\S+)/g).map((part, i) =>
    /^@\S+$/.test(part)
      ? <span key={i}
          style={{ fontWeight: 700, color: '#D97706', background: '#FEF3C7', borderRadius: 3, padding: '0 3px', cursor: onMentionClick ? 'pointer' : 'default' }}
          onClick={onMentionClick ? e => { e.stopPropagation(); onMentionClick(); } : undefined}
          title={onMentionClick ? 'Répondre à l\'auteur' : undefined}
        >{part}</span>
      : part
  );
}

const RECORD_TYPE_OPTIONS = [
  { key: 'entreprise', label: 'Annuaire' },
  { key: 'achat',      label: 'Achat' },
  { key: 'location',   label: 'Location' },
  { key: 'atelier',    label: 'Atelier' },
  { key: 'materiel',   label: 'Matériel' },
  { key: 'autre',      label: 'Autre' },
];

// ─── Combobox entreprise ───────────────────────────────────────────────────────

function EntrepriseCombobox({ value, label, onChange }) {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    api.getEntreprises({ search: query || '', per_page: 30 })
      .then(r => { if (active) setOptions(r.data?.items || r.data || []); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, query]);

  const inp = { border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px', fontSize: 13, outline: 'none', width: '100%', background: 'var(--surface)' };

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen(v => !v)} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: label ? 'var(--text)' : 'var(--text-muted)' }}>{label || 'Rechercher une entreprise…'}</span>
        <ChevronDown size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      </div>
      {open && (
        <div style={{ position: 'absolute', zIndex: 9999, top: '100%', left: 0, right: 0, marginTop: 4, background: 'white', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
          <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher…"
            style={{ width: '100%', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border-light)', fontSize: 13, outline: 'none' }} />
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {loading
              ? <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Chargement…</div>
              : options.length === 0
                ? <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Aucun résultat</div>
                : options.map(e => (
                    <div key={e.entreprise_id} onClick={() => { onChange(e); setOpen(false); setQuery(''); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border-light)' }}
                      onMouseEnter={ev => ev.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                      {e.nom}
                    </div>
                  ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal création discussion ─────────────────────────────────────────────────

function ModalCreateDiscussion({ onClose, onCreated }) {
  const [recordType,    setRecordType]    = useState('entreprise');
  const [recordId,      setRecordId]      = useState('');
  const [recordLabel,   setRecordLabel]   = useState('');
  const [message,       setMessage]       = useState('');
  const [users,         setUsers]         = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [saving,        setSaving]        = useState(false);

  useEffect(() => {
    api.getUsers().then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!recordLabel.trim()) { toast.error('Sélectionnez ou nommez la fiche concernée'); return; }
    if (!message.trim())     { toast.error('Écrivez un premier message'); return; }
    setSaving(true);
    try {
      const r = await api.createDiscussion({
        record_type:     recordType,
        record_id:       recordId || `${recordType}-${Date.now()}`,
        record_label:    recordLabel.trim(),
        first_message:   message.trim(),
        participant_ids: selectedUsers.map(u => u.user_id),
      });
      toast.success('Discussion créée');
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally { setSaving(false); }
  };

  const inp = { border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px', fontSize: 13, outline: 'none', width: '100%', background: 'var(--surface)', color: 'var(--text)' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 500, padding: '26px 26px 22px' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Nouvelle discussion</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={17} /></button>
        </div>

        <div className="space-y-4">
          {/* Type de fiche */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Type de fiche</label>
            <div className="flex gap-2 flex-wrap">
              {RECORD_TYPE_OPTIONS.map(opt => (
                <button key={opt.key} onClick={() => { setRecordType(opt.key); setRecordId(''); setRecordLabel(''); }}
                  style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${recordType === opt.key ? '#1D4ED8' : 'var(--border)'}`, background: recordType === opt.key ? '#EFF6FF' : 'white', color: recordType === opt.key ? '#1D4ED8' : 'var(--text-muted)' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fiche */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Fiche concernée <span style={{ color: '#DC2626' }}>*</span>
            </label>
            {recordType === 'entreprise' ? (
              <EntrepriseCombobox
                value={recordId} label={recordLabel}
                onChange={e => { setRecordId(e.entreprise_id); setRecordLabel(e.nom); }}
              />
            ) : (
              <input value={recordLabel} onChange={e => setRecordLabel(e.target.value)}
                placeholder={`Nom / numéro ${RECORD_TYPE_OPTIONS.find(o => o.key === recordType)?.label || ''}…`}
                style={inp} />
            )}
          </div>

          {/* Premier message */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Message <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Écrivez le premier message de la discussion…"
              rows={3} style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }}
              data-testid="discussion-message-input" />
          </div>

          {/* Notifier */}
          {users.length > 0 && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                <UserPlus size={11} style={{ display: 'inline', marginRight: 4 }} />
                Notifier des collègues (optionnel)
              </label>
              <div className="flex flex-wrap gap-2">
                {users.filter(u => u.actif !== false).map(u => {
                  const sel = selectedUsers.some(s => s.user_id === u.user_id);
                  return (
                    <button key={u.user_id}
                      onClick={() => setSelectedUsers(prev => sel ? prev.filter(s => s.user_id !== u.user_id) : [...prev, u])}
                      style={{ padding: '3px 10px', borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${sel ? '#1D4ED8' : 'var(--border)'}`, background: sel ? '#EFF6FF' : 'white', color: sel ? '#1D4ED8' : 'var(--text-muted)' }}>
                      {u.name || u.email}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving} data-testid="create-discussion-btn"
            style={{ padding: '7px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, color: 'white', background: saving ? '#9CA3AF' : '#1D4ED8', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            {saving ? 'Création…' : 'Créer la discussion'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Card principale ───────────────────────────────────────────────────────────

const NAV_MAP = {
  vente:      (id) => `/ventes?open_devis=${id}`,
  location:   (id) => `/contrats?contrat_id=${id}`,
  location_v2: (id) => `/location-v2/contrats?contrat_id=${id}`,
  atelier:    (id) => `/atelier?open_or=${id}`,
  reparation: (id) => `/atelier?open_or=${id}`, // alias legacy
  achat:      (id) => `/achats?open_achat=${id}`,
  entreprise: (id) => `/annuaire?open_ent=${id}`,
  materiel:   (id) => `/materiels?materiel=${id}`,
};

export function DiscussionsCard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const PAGE_SIZE = 15;

  const [tab,             setTab]             = useState('suivis');
  const [filterUnread,    setFilterUnread]    = useState(false);
  const [filterMentioned, setFilterMentioned] = useState(false);
  const [page,          setPage]          = useState(0);
  const [threads,       setThreads]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [expandedId,    setExpandedId]    = useState(null);
  const [messagesMap,   setMessagesMap]   = useState({});
  const [loadingMsgs,   setLoadingMsgs]   = useState(null);
  const [replyMap,      setReplyMap]      = useState({});
  const [sendingId,     setSendingId]     = useState(null);
  const [showCreate,    setShowCreate]    = useState(false);
  const [allUsers,      setAllUsers]      = useState([]);
  const [inviteOpen,    setInviteOpen]    = useState(null);
  const [inviteQuery,   setInviteQuery]   = useState('');
  const [invitePos,     setInvitePos]     = useState(null);
  const [pinningId,     setPinningId]     = useState(null);
  const [mentionsMap,   setMentionsMap]   = useState({}); // { [threadId]: [{user_id, user_nom}] }
  const [mentionState,  setMentionState]  = useState(null); // { threadId, query } | null
  const textareaRefs   = useRef({});
  const inviteBtnRefs = useRef({});

  // Fermer le dropdown invite au clic en dehors
  useEffect(() => {
    const h = (e) => {
      if (!inviteOpen) return;
      const btn = inviteBtnRefs.current[inviteOpen];
      if (btn && btn.contains(e.target)) return;
      // Vérifier si le clic est dans le dropdown (via data-invite-dropdown)
      if (e.target.closest?.('[data-invite-dropdown]')) return;
      setInviteOpen(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [inviteOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getDiscussions({ show_in_dashboard: tab === 'suivis' });
      setThreads(r.data || []);
    } catch { }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Charger la liste des utilisateurs une seule fois
  useEffect(() => {
    api.getUsers().then(r => setAllUsers(r.data || [])).catch(() => {});
  }, []);

  const handleExpand = async (threadId) => {
    if (expandedId === threadId) { setExpandedId(null); return; }
    setExpandedId(threadId);
    // Marquer comme lu à l'ouverture
    api.markDiscussionRead(threadId).catch(() => {});
    if (messagesMap[threadId]) return;
    setLoadingMsgs(threadId);
    try {
      const r = await api.getDiscussionMessages(threadId);
      setMessagesMap(prev => ({ ...prev, [threadId]: r.data || [] }));
    } catch { }
    finally { setLoadingMsgs(null); }
  };

  const handleReply = async (threadId) => {
    const corps = (replyMap[threadId] || '').trim();
    if (!corps) return;
    // Validation : garder uniquement les mentions dont le @Prénom est encore dans le texte
    const rawMentions = mentionsMap[threadId] || [];
    const mentions = rawMentions.filter(m => {
      const prenom = firstPrenom(m.user_nom || '');
      return prenom && corps.includes(`@${prenom}`);
    });
    setSendingId(threadId);
    try {
      const r = await api.postDiscussionMessage(threadId, corps, mentions);
      setMessagesMap(prev => ({ ...prev, [threadId]: [...(prev[threadId] || []), r.data] }));
      setReplyMap(prev => ({ ...prev, [threadId]: '' }));
      setMentionsMap(prev => ({ ...prev, [threadId]: [] }));
      // Mise à jour preview dans la liste
      setThreads(prev => prev.map(t => t.thread_id === threadId
        ? { ...t, last_message_corps: corps, last_message_auteur_nom: user?.name || 'Moi', last_message_at: new Date().toISOString(), nb_messages: (t.nb_messages || 0) + 1 }
        : t
      ));
    } catch { toast.error('Erreur envoi'); }
    finally { setSendingId(null); }
  };

  const handleToggleFollow = async (threadId, currentVal) => {
    const newVal = !currentVal;
    try {
      await api.toggleDiscussionFollow(threadId, newVal);
      if (tab === 'suivis' && !newVal) {
        setThreads(prev => prev.filter(t => t.thread_id !== threadId));
        if (expandedId === threadId) setExpandedId(null);
        toast.success('Discussion mise en silence');
      } else if (tab === 'silence' && newVal) {
        setThreads(prev => prev.filter(t => t.thread_id !== threadId));
        toast.success('Discussion suivie à nouveau');
      }
    } catch { toast.error('Erreur'); }
  };

  const handleInvite = async (threadId, userId) => {
    try {
      await api.inviteToDiscussion(threadId, [userId]);
      setInviteOpen(null);
      setInviteQuery('');
      toast.success('Participant ajouté à la discussion');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur invitation');
    }
  };

  const insertMentionInCard = (threadId, u) => {
    const text   = replyMap[threadId] || '';
    const el     = textareaRefs.current[threadId];
    const cursor = el?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const atIdx  = before.lastIndexOf('@');
    const prenom = firstPrenom(u.user_nom || u.name || '');
    const newText = text.slice(0, atIdx) + `@${prenom} ` + text.slice(cursor);
    setReplyMap(prev => ({ ...prev, [threadId]: newText }));
    setMentionsMap(prev => ({
      ...prev,
      [threadId]: [...(prev[threadId] || []).filter(m => m.user_id !== u.user_id), { user_id: u.user_id, user_nom: u.user_nom || u.name }],
    }));
    setMentionState(null);
    setTimeout(() => {
      if (el) { el.focus(); const p = atIdx + prenom.length + 2; el.setSelectionRange(p, p); }
    }, 10);
  };

  const handleTogglePin = async (threadId, currentVal) => {    const newVal = !currentVal;
    setPinningId(threadId);
    try {
      await api.toggleDiscussionPin(threadId, newVal);
      setThreads(prev => {
        const updated = prev.map(t => t.thread_id === threadId ? { ...t, is_pinned: newVal } : t);
        // Remettre les épinglés en tête
        return [...updated.filter(t => t.is_pinned), ...updated.filter(t => !t.is_pinned)];
      });
    } catch (e) {
      toast.error('Erreur lors de l\'épinglage');
    } finally { setPinningId(null); }
  };

  // Reset page quand tab ou filtre change
  useEffect(() => { setPage(0); setExpandedId(null); }, [tab, filterUnread, filterMentioned]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const filteredThreads  = threads
    .filter(t => !filterUnread    || t.unread_count > 0)
    .filter(t => !filterMentioned || t.is_mentioned_unread);
  const totalPages       = Math.ceil(filteredThreads.length / PAGE_SIZE);
  const displayedThreads = filteredThreads.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const unreadTotal      = threads.filter(t => t.unread_count > 0).length;
  const mentionedTotal   = threads.filter(t => t.is_mentioned_unread).length;

  return (
    <>
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        {/* Header */}
        <div className="px-3 pt-3 pb-2.5">
          {/* Ligne 1 : titre + badge + non lus + (desktop: pagination + toggle) */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <MessageCircle size={16} style={{ color: '#1D4ED8', flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>Discussions</span>
              {threads.length > 0 && (
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#1D4ED8', color: '#fff', flexShrink: 0 }}>
                  {threads.length}
                </span>
              )}
              {/* Filtre Non lus — visible sur toutes tailles */}
              {unreadTotal > 0 && (
                <button
                  onClick={() => setFilterUnread(v => !v)}
                  data-testid="discussion-filter-unread"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${filterUnread ? '#1D4ED8' : 'var(--border)'}`,
                    background: filterUnread ? '#EFF6FF' : 'transparent',
                    color: filterUnread ? '#1D4ED8' : 'var(--text-muted)',
                    flexShrink: 0,
                  }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1D4ED8', display: 'block', flexShrink: 0 }} />
                  Non lus ({unreadTotal})
                </button>
              )}
              {/* Filtre Mentions */}
              {mentionedTotal > 0 && (
                <button
                  onClick={() => setFilterMentioned(v => !v)}
                  data-testid="discussion-filter-mentioned"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${filterMentioned ? '#D97706' : 'var(--border)'}`,
                    background: filterMentioned ? '#FFFBEB' : 'transparent',
                    color: filterMentioned ? '#D97706' : 'var(--text-muted)',
                    flexShrink: 0,
                  }}>
                  <span style={{ fontWeight: 800, fontSize: 12, lineHeight: 1 }}>@</span>
                  Mentions ({mentionedTotal})
                </button>
              )}
            </div>
            {/* Desktop uniquement : toggle + pagination sur même ligne */}
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ background: 'var(--bg-muted, #f3f4f6)' }}>
                {[{ key: 'suivis', label: 'Suivis' }, { key: 'silence', label: 'Silence' }].map(t => (
                  <button key={t.key} onClick={() => { setTab(t.key); setExpandedId(null); }}
                    data-testid={`discussion-toggle-${t.key}`}
                    className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                    style={{ background: tab === t.key ? '#1D4ED8' : 'transparent', color: tab === t.key ? '#fff' : 'var(--text-muted)' }}>
                    {t.label}
                  </button>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-0.5">
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                    data-testid="discussion-page-prev"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)', background: 'white', color: page === 0 ? 'var(--text-disabled)' : 'var(--text-muted)', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>
                    <ChevronLeft size={12} />
                  </button>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', padding: '0 4px', whiteSpace: 'nowrap' }}>{page + 1}/{totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                    data-testid="discussion-page-next"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)', background: 'white', color: page >= totalPages - 1 ? 'var(--text-disabled)' : 'var(--text-muted)', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>
                    <ChevronRight size={12} />
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Ligne 2 mobile uniquement : toggle + pagination */}
          <div className="flex sm:hidden items-center justify-between mt-2 gap-2">
            <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ background: 'var(--bg-muted, #f3f4f6)' }}>
              {[{ key: 'suivis', label: 'Suivis' }, { key: 'silence', label: 'Silence' }].map(t => (
                <button key={t.key} onClick={() => { setTab(t.key); setExpandedId(null); }}
                  data-testid={`discussion-toggle-${t.key}-mobile`}
                  className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                  style={{ background: tab === t.key ? '#1D4ED8' : 'transparent', color: tab === t.key ? '#fff' : 'var(--text-muted)' }}>
                  {t.label}
                </button>
              ))}
            </div>
            {totalPages > 1 ? (
              <div className="flex items-center gap-0.5">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  data-testid="discussion-page-prev-mobile"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)', background: 'white', color: page === 0 ? 'var(--text-disabled)' : 'var(--text-muted)', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>
                  <ChevronLeft size={12} />
                </button>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', padding: '0 4px', whiteSpace: 'nowrap' }}>{page + 1}/{totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                  data-testid="discussion-page-next-mobile"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)', background: 'white', color: page >= totalPages - 1 ? 'var(--text-disabled)' : 'var(--text-muted)', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>
                  <ChevronRight size={12} />
                </button>
              </div>
            ) : <div />}
          </div>
        </div>

        {/* Séparateur */}
        <div style={{ height: 1, background: 'var(--border)' }} />

        {/* Contenu */}
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin" style={{ color: '#1D4ED8' }} />
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <Inbox size={28} style={{ color: 'var(--text-disabled)' }} />
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {filterUnread
                ? 'Aucun message non lu'
                : tab === 'suivis' ? 'Aucune discussion suivie' : 'Aucune discussion en silence'}
            </p>
          </div>
        ) : (
          <div>
            {displayedThreads.map((t, i) => {
              const isExpanded = expandedId === t.thread_id;
              const messages   = messagesMap[t.thread_id] || [];
              const reply      = replyMap[t.thread_id] || '';

              return (
                <div key={t.thread_id} style={{ borderTop: i > 0 ? '1px solid var(--border-light)' : 'none' }}>
                  {/* Ligne thread */}
                  <div className="flex items-center gap-2 px-3 py-2.5"
                    data-testid={`discussion-row-${t.thread_id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleExpand(t.thread_id)}>
                    {/* Indicateur couleur + badge non-lus */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.unread_count > 0 ? '#1D4ED8' : '#3B82F6', display: 'block' }} />
                      {t.unread_count > 0 && (
                        <span style={{
                          position: 'absolute', top: -4, right: -8,
                          minWidth: 14, height: 14, borderRadius: 999,
                          background: '#1D4ED8', color: 'white',
                          fontSize: 9, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 3px',
                        }}>
                          {t.unread_count > 9 ? '9+' : t.unread_count}
                        </span>
                      )}
                    </div>

                    {/* Fiche + preview */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {/* Seul le record_label navigue vers la fiche */}
                        <span
                          data-testid={`discussion-nav-${t.thread_id}`}
                          onClick={e => {
                            e.stopPropagation();
                            const path = t.record_id && NAV_MAP[t.record_type]
                              ? NAV_MAP[t.record_type](t.record_id)
                              : null;
                            if (path) navigate(path);
                          }}
                          style={{ fontSize: 12, fontWeight: 700, color: '#1D4ED8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' }}>
                          {t.record_label}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, background: 'var(--bg-muted,#f9fafb)', padding: '0 5px', borderRadius: 4 }}>
                          {RECORD_TYPE_OPTIONS.find(o => o.key === t.record_type)?.label
                            || (t.record_type === 'location_v2' ? 'Location V2' : t.record_type)}
                        </span>
                      </div>
                      {t.last_message_corps && (
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                          <span style={{ fontWeight: 600 }}>
                            <span className="sm:hidden">{firstPrenom(t.last_message_auteur_nom)}</span>
                            <span className="hidden sm:inline">{t.last_message_auteur_nom}</span>
                            {' : '}
                          </span>
                          {renderMentions(t.last_message_corps)}
                        </p>
                      )}
                    </div>

                    {/* Méta : épingle + badge mentionné + heure */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {t.is_mentioned_unread && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#FEF3C7', color: '#D97706', border: '1px solid #FDE68A', whiteSpace: 'nowrap' }}>
                          Mentionné
                        </span>
                      )}
                      {t.is_pinned && (
                        <Pin size={10} style={{ color: '#D97706', transform: 'rotate(45deg)' }} />
                      )}
                      {t.nb_messages > 0 && (
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{fmtMsgTime(t.last_message_at)}</span>
                      )}
                    </div>
                  </div>

                  {/* Accordéon — messages + réponse */}
                  {isExpanded && (
                    <div style={{ background: '#F8FAFF', borderTop: '1px solid #DBEAFE', padding: '12px 16px', borderBottom: '1px solid #DBEAFE' }}>

                      {/* Barre Ajouter participant + Sourdine + Épingler */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 8 }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleTogglePin(t.thread_id, t.is_pinned); }}
                          title={t.is_pinned ? 'Désépingler' : 'Épingler en haut'}
                          data-testid={`pin-btn-${t.thread_id}`}
                          disabled={pinningId === t.thread_id}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: `1px solid ${t.is_pinned ? '#F59E0B' : 'var(--border)'}`, background: t.is_pinned ? '#FFFBEB' : 'white', color: t.is_pinned ? '#D97706' : 'var(--text-muted)', fontSize: 11, cursor: 'pointer', fontWeight: t.is_pinned ? 600 : 400 }}>
                          {t.is_pinned ? <PinOff size={11} /> : <Pin size={11} />}
                          {t.is_pinned ? 'Désépingler' : 'Épingler'}
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleToggleFollow(t.thread_id, t.show_in_dashboard); }}
                          title={tab === 'suivis' ? 'Mettre en silence' : 'Suivre à nouveau'}
                          data-testid={`follow-btn-${t.thread_id}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'white', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>
                          {tab === 'suivis' ? <BellOff size={11} /> : <Bell size={11} style={{ color: '#1D4ED8' }} />}
                          {tab === 'suivis' ? 'Sourdine' : 'Suivre'}
                        </button>
                        <button
                          ref={el => inviteBtnRefs.current[t.thread_id] = el}
                          onClick={() => {
                            if (inviteOpen === t.thread_id) { setInviteOpen(null); return; }
                            const btn = inviteBtnRefs.current[t.thread_id];
                            if (btn) {
                              const rect = btn.getBoundingClientRect();
                              setInvitePos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setInviteOpen(t.thread_id);
                            setInviteQuery('');
                          }}
                          data-testid={`invite-btn-${t.thread_id}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid #BFDBFE', background: 'white', color: '#1D4ED8', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          <UserPlus size={11} /> Ajouter
                        </button>
                        {inviteOpen === t.thread_id && invitePos && (
                          <div data-invite-dropdown style={{ position: 'fixed', top: invitePos.top, right: invitePos.right, zIndex: 9999, background: 'white', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: 220 }}>
                            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Search size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                              <input autoFocus value={inviteQuery} onChange={e => setInviteQuery(e.target.value)}
                                placeholder="Rechercher…"
                                style={{ border: 'none', outline: 'none', fontSize: 12, flex: 1, background: 'transparent', color: 'var(--text)' }} />
                            </div>
                            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                              {allUsers.filter(u => u.actif !== false && (u.name || u.email || '').toLowerCase().includes(inviteQuery.toLowerCase())).map(u => (
                                <div key={u.user_id}
                                  onClick={() => handleInvite(t.thread_id, u.user_id)}
                                  style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
                                  onMouseEnter={ev => ev.currentTarget.style.background = '#EFF6FF'}
                                  onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                                  {u.name || u.email}
                                </div>
                              ))}
                              {allUsers.filter(u => u.actif !== false && (u.name || u.email || '').toLowerCase().includes(inviteQuery.toLowerCase())).length === 0 && (
                                <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>Aucun résultat</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {loadingMsgs === t.thread_id ? (
                        <div className="flex justify-center py-4">
                          <Loader2 size={16} className="animate-spin" style={{ color: '#1D4ED8' }} />
                        </div>
                      ) : messages.length === 0 ? (
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>Aucun message</p>
                      ) : (
                        <div className="space-y-2 mb-3">
                          {messages.map(m => (
                            <div key={m.message_id} style={{ fontSize: 13 }}>
                              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{fmtMsgTime(m.created_at)} · </span>
                              <span style={{ fontWeight: 700, color: '#1D4ED8' }}>
                                <span className="sm:hidden">{firstPrenom(m.auteur_nom)}</span>
                                <span className="hidden sm:inline">{m.auteur_nom}</span>
                                {' : '}
                              </span>
                              <span style={{ color: 'var(--text)', whiteSpace: 'pre-wrap' }}>
                                {renderMentions(m.corps, m.auteur_id !== (user?.user_id || '') ? () => {
                                  const prenom = firstPrenom(m.auteur_nom || '');
                                  if (prenom) {
                                    setReplyMap(prev => ({ ...prev, [t.thread_id]: `@${prenom} ` }));
                                    setMentionsMap(prev => ({ ...prev, [t.thread_id]: [{ user_id: m.auteur_id, user_nom: m.auteur_nom }] }));
                                    textareaRefs.current[t.thread_id]?.focus();
                                  }
                                } : null)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Réponse avec @mention */}
                      <div style={{ position: 'relative', borderTop: messages.length > 0 ? '1px solid #DBEAFE' : 'none', paddingTop: messages.length > 0 ? 10 : 0 }}>
                        {/* Dropdown @mention */}
                        {mentionState?.threadId === t.thread_id && (
                          <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 44, zIndex: 9999, marginBottom: 3, background: 'white', border: '1px solid #FDE68A', borderRadius: 8, boxShadow: '0 -4px 16px rgba(0,0,0,0.1)', maxHeight: 150, overflowY: 'auto' }}>
                            {allUsers
                              .filter(u => u.actif !== false && firstPrenom(u.name || u.email || '').toLowerCase().includes(mentionState.query.toLowerCase()))
                              .slice(0, 5)
                              .map(u => (
                                <div key={u.user_id}
                                  onMouseDown={e => { e.preventDefault(); insertMentionInCard(t.thread_id, { user_id: u.user_id, user_nom: u.name || u.email }); }}
                                  style={{ padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #FEF3C7' }}
                                  onMouseEnter={ev => ev.currentTarget.style.background = '#FEF3C7'}
                                  onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                                  <span style={{ fontWeight: 800, color: '#D97706' }}>@</span>
                                  <span style={{ fontWeight: 600 }}>{firstPrenom(u.name || u.email || '')}</span>
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{u.name || u.email}</span>
                                </div>
                              ))
                            }
                            {allUsers.filter(u => u.actif !== false && firstPrenom(u.name || u.email || '').toLowerCase().includes((mentionState.query || '').toLowerCase())).length === 0 && (
                              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>Aucun utilisateur</div>
                            )}
                          </div>
                        )}
                        <div className="flex items-end gap-2">
                          <textarea
                            value={reply}
                            ref={el => textareaRefs.current[t.thread_id] = el}
                            onChange={e => {
                              const val = e.target.value;
                              setReplyMap(prev => ({ ...prev, [t.thread_id]: val }));
                              const cursor = e.target.selectionStart;
                              const match = val.slice(0, cursor).match(/@(\w*)$/);
                              if (match) setMentionState({ threadId: t.thread_id, query: match[1] });
                              else if (mentionState?.threadId === t.thread_id) setMentionState(null);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Escape') setMentionState(null);
                              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(t.thread_id); }
                            }}
                            onBlur={() => setTimeout(() => setMentionState(null), 150)}
                            placeholder="Répondre… (@nom pour mentionner)"
                            data-testid={`reply-input-${t.thread_id}`}
                            rows={2}
                            style={{ flex: 1, border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 10px', fontSize: 13, outline: 'none', background: 'white', resize: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
                          />
                          <button
                            onClick={() => handleReply(t.thread_id)}
                            disabled={!reply.trim() || sendingId === t.thread_id}
                            data-testid={`reply-send-${t.thread_id}`}
                            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: reply.trim() ? '#1D4ED8' : '#E5E7EB', color: reply.trim() ? 'white' : '#9CA3AF', cursor: reply.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                            {sendingId === t.thread_id
                              ? <Loader2 size={12} className="animate-spin" />
                              : <Send size={12} />
                            }
                            Envoyer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* showCreate retiré — création depuis les fiches uniquement */}
    </>
  );
}
